/* global NDEFReader */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setToken } from '../features/auth/authSlice';
import { initChecks } from '../features/faceCheck/faceCheckSlice';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import Logo from '../assets/gorion.png';
import API_BASE_URL from '../config';
import FaceLiveness from '../components/FaceRecognition/FaceLiveness';
import EnrollmentStation from '../components/FaceRecognition/EnrollmentStation';
import '../components/FaceRecognition/FaceCheckModal.css';

const Login = () => {
  const [activeTab, setActiveTab] = useState('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [faceError, setFaceError]           = useState(null);
  const [showEnrollment, setShowEnrollment] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Restaurar credenciales guardadas
  useEffect(() => {
    const saved = localStorage.getItem('orion_remember');
    if (saved) {
      try {
        const { email: savedEmail, tab } = JSON.parse(saved);
        setEmail(savedEmail || '');
        setActiveTab(tab || 'password');
        setRememberMe(true);
      } catch (_) { /* silencioso */ }
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}auth/login`, { email, password });
      const { token } = response.data;

      // Guardar o limpiar remember me
      if (rememberMe) {
        localStorage.setItem('orion_remember', JSON.stringify({ email, tab: activeTab }));
      } else {
        localStorage.removeItem('orion_remember');
      }

      dispatch(setToken(token));

      // Si es coordinador, programar los 3 checks faciales del turno
      try {
        const base64 = token.split('.')[1];
        const payload = JSON.parse(atob(base64.replace(/-/g, '+').replace(/_/g, '/')));
        if (payload.rol === 'colaborador') {
          dispatch(initChecks(Date.now()));
        }
      } catch { /* silencioso */ }

      navigate('/inicio');
    } catch (error) {
      console.error('Error en el inicio de sesión:', error);
      alert('Credenciales inválidas');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Se llama cuando FaceLiveness confirma el parpadeo y extrae el descriptor.
   * Envía email + descriptor al backend para login facial.
   */
  const handleFaceLoginConfirmed = async (descriptor) => {
    setLoading(true);
    setFaceError(null);
    try {
      const response = await axios.post(`${API_BASE_URL}auth/login-face`, { descriptor });
      const { token } = response.data;
      dispatch(setToken(token));
      dispatch(initChecks(Date.now()));
      navigate('/inicio');
    } catch (error) {
      const msg = error.response?.data?.error || 'Rostro no reconocido. Intenta de nuevo.';
      setFaceError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleNFCLogin = async () => {
    setLoading(true);
    try {
      if (!('NDEFReader' in window)) {
        alert('Tu navegador no soporta NFC');
        return;
      }

      const ndef = new NDEFReader();
      await ndef.scan();

      ndef.onreading = async (event) => {
        const { serialNumber } = event;
        const response = await axios.post(`${API_BASE_URL}auth/login-nfc`, { uid: serialNumber });
        const { token } = response.data;

        dispatch(setToken(token));
        navigate('/inicio');
      };
    } catch (err) {
      console.error('Error con NFC:', err.message);
      alert('Error al leer NFC');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Fondo animado */}
      <div className="login-bg">
        <div className="login-bg-orb login-bg-orb-1" />
        <div className="login-bg-orb login-bg-orb-2" />
        <div className="login-bg-orb login-bg-orb-3" />
        <div className="login-bg-grid" />
      </div>

      <div className="login-card">
        {/* Header con logo */}
        <div className="login-card-header">
          <img src={Logo} alt="ORION Logo" className="login-logo" />
          <h1 className="login-brand">Grupo Orión</h1>
          <span className="login-product">MINTAKA CORE</span>
        </div>

        {/* Tabs */}
        <div className="login-tabs">
          <button
            className={`login-tab ${activeTab === 'password' ? 'login-tab-active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            <i className="fa fa-lock login-tab-icon" />
            Coordinación
          </button>
          <button
            className={`login-tab ${activeTab === 'nfc' ? 'login-tab-active' : ''}`}
            onClick={() => setActiveTab('nfc')}
          >
            <i className="fa fa-id-card login-tab-icon" />
            No. Colaborador
          </button>
          <button
            className={`login-tab ${activeTab === 'face' ? 'login-tab-active' : ''}`}
            onClick={() => { setActiveTab('face'); setFaceError(null); }}
          >
            <i className="fa fa-camera login-tab-icon" />
            Rostro
          </button>
        </div>

        {/* Body */}
        <div className="login-card-body">
          {loading ? (
            <div className="login-loader">
              <div className="login-loader-ring" />
              <p className="login-loader-text">Verificando identidad...</p>
            </div>
          ) : activeTab === 'face' ? (
            /* ── Tab Reconocimiento Facial ── */
            <div className="login-form">
              {faceError && (
                <div className="fcheck-error" style={{ marginBottom: 12 }}>
                  <i className="fa fa-exclamation-triangle" /> {faceError}
                  <button
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: 12 }}
                    onClick={() => setFaceError(null)}
                  >
                    Reintentar
                  </button>
                </div>
              )}
              <FaceLiveness
                title=""
                onConfirmed={handleFaceLoginConfirmed}
                onError={(e) => setFaceError(e)}
              />
            </div>
          ) : activeTab === 'password' ? (
            <form onSubmit={handleLogin} className="login-form">
              <div className="login-field">
                <label className="login-label">Correo electrónico</label>
                <div className="login-input-wrap">
                  <i className="fa fa-envelope login-input-icon" />
                  <input
                    type="email"
                    placeholder="ejemplo@correo.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label">Contraseña</label>
                <div className="login-input-wrap">
                  <i className="fa fa-key login-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>

              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="login-remember-check" />
                <span className="login-remember-text">Recordar mis datos</span>
              </label>

              <button type="submit" className="login-btn">
                Iniciar sesión
                <i className="fa fa-arrow-right login-btn-arrow" />
              </button>
            </form>
          ) : (
            <form onSubmit={handleLogin} className="login-form">
              <div className="login-field">
                <label className="login-label">No. Colaborador</label>
                <div className="login-input-wrap">
                  <i className="fa fa-user login-input-icon" />
                  <input
                    type="number"
                    placeholder="Ej. 1842"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="login-field">
                <label className="login-label">Contraseña</label>
                <div className="login-input-wrap">
                  <i className="fa fa-key login-input-icon" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="login-eye-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <i className={`fa ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
              </div>

              <label className="login-remember">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="login-remember-check" />
                <span className="login-remember-text">Recordar mis datos</span>
              </label>

              <button type="submit" className="login-btn">
                Iniciar sesión
                <i className="fa fa-arrow-right login-btn-arrow" />
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="login-card-footer">
          <span className="login-footer-text">Powered by Grupo Orión</span>
          <button
            className="login-enroll-link"
            onClick={() => setShowEnrollment(true)}
          >
            Registrar coordinador
          </button>
        </div>
      </div>

      {showEnrollment && (
        <EnrollmentStation onClose={() => setShowEnrollment(false)} />
      )}
    </div>
  );
};

export default Login;
